/**
 * The customers' glossary, read by the desk that answers them.
 *
 * WHY THIS FETCHES INSTEAD OF HOLDING A COPY.
 *
 * The glossary is canon and canon lives in the platform repository. Copying it
 * here would be the second divergent definition this codebase keeps paying
 * for -- and a vendored copy with a recorded checksum is worse than useless,
 * because it verifies only that nobody edited it HERE while saying nothing
 * about the canon moving there.
 *
 * scripts/build_help_centre.js in the platform repo writes
 * public/help-centre.json from the two glossary documents. That file is served
 * with the platform, so this reads exactly what the customer is looking at
 * while they are on the phone.
 *
 * THERE IS NO FALLBACK, DELIBERATELY.
 *
 * A support desk quoting last month's definition of Flex Balance -- the one
 * that says Orchid holds the money, which the Expansion corrected because it
 * is the No Wallet Ruling's opposite -- is worse than a support desk saying it
 * cannot reach the glossary. So a failed fetch says so and offers nothing.
 */

/** Where the platform is served. Set REACT_APP_PLATFORM_URL per environment. */
export const platformOrigin = () => (
  process.env.REACT_APP_PLATFORM_URL || 'https://orchidholdings.co.za'
).replace(/\/+$/, '');

export const helpCentreUrl = () => `${platformOrigin()}/help-centre.json`;

/**
 * Fetch the merged glossary.
 *
 * Returns { data, error }. `data` is null whenever anything went wrong, and
 * `error` is a sentence a staff member can act on rather than a status code.
 */
export async function fetchHelpCentre() {
  let response;
  try {
    response = await fetch(helpCentreUrl(), { headers: { Accept: 'application/json' } });
  } catch {
    return {
      data: null,
      error: 'The glossary could not be reached. It is served with the platform, '
        + 'so this usually means the platform is down rather than the Portal.',
    };
  }

  if (!response.ok) {
    return {
      data: null,
      error: `The glossary answered ${response.status}. Nothing is shown rather than `
        + 'something out of date.',
    };
  }

  let body;
  try {
    body = await response.json();
  } catch {
    return { data: null, error: 'The glossary came back in a form this screen could not read.' };
  }

  if (!body || !Array.isArray(body.glossary) || body.glossary.length === 0) {
    return { data: null, error: 'The glossary came back empty, which is never correct.' };
  }

  return { data: body, error: null };
}

/**
 * Search terms, then definitions. Somebody typing "deposit" wants Deposit
 * Builder above the several entries that merely mention a deposit.
 */
export function searchTerms(glossary, query) {
  const rows = Array.isArray(glossary) ? glossary : [];
  const q = (query || '').trim().toLowerCase();
  if (!q) return rows;
  const inTerm = rows.filter((g) => g.term.toLowerCase().includes(q));
  const inBody = rows.filter(
    (g) => !g.term.toLowerCase().includes(q) && g.definition.toLowerCase().includes(q),
  );
  return [...inTerm, ...inBody];
}
